'use client';

import React, { useState, useEffect } from 'react';
import { QuestionStepProps } from '@/sections/forms/progressive-form/steps/question-step/types';
import { Button } from '@/components/button/Button';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import { useTranslations } from '@/lib/locale-context';

const QuestionStep: React.FC<QuestionStepProps> = ({
  type,
  headline,
  description,
  answers = [],
  selectedValue,
  onChange,
  onNext,
  onBack,
  step,
  totalSteps,
}) => {
  const { t } = useTranslations();
  const [error, setError] = useState('');

  const isValid =
    type === 'radio' || type === 'select'
      ? typeof selectedValue === 'string' && selectedValue !== ''
      : type === 'input'
        ? typeof selectedValue === 'string' && selectedValue.trim() !== ''
        : type === 'checkbox'
          ? Array.isArray(selectedValue) && selectedValue.length > 0
          : true;

  useEffect(() => {
    if (isValid) {
      setError('');
    }
  }, [selectedValue, isValid]);

  const handleNextClick = () => {
    if (!isValid) {
      setError(t('progressiveForm.requiredField'));
      return;
    }
    onNext();
  };

  return (
    <div className="mx-auto flex w-full flex-col rounded-lg bg-white shadow-sm lg:w-2/3">
      {/* Content Section */}
      <div className="flex flex-col px-8 py-6">
        {/* Step Indicator */}
        <div className="flex items-center justify-between gap-2 border-b border-b-neutral-50 pb-4">
          <div className="flex flex-col space-y-2 lg:w-2/3">
            <label className="w-full text-[20px] font-medium text-brand-700">
              {headline}
            </label>
            {description && (
              <SerializeDescription
                description={description}
                className="mb-4 text-[18px] font-normal text-brand-700"
              />
            )}
          </div>
          <span className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-3 py-1 text-center text-sm text-gray-600">
            {/* Compact "1/4" on mobile, full label from md up */}
            <span className="md:hidden">{`${step}/${totalSteps}`}</span>
            <span className="hidden md:inline">
              {t('progressiveForm.step', { current: step, total: totalSteps })}
            </span>
          </span>
        </div>

        {/* Input */}
        <div>
          {type === 'radio' && (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {answers.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="radio"
                    name={`question-${step}`}
                    value={option.value}
                    checked={selectedValue === option.value}
                    onChange={() => onChange(option.value)}
                    className="form-radio text-blue-600"
                  />
                  {option.value}
                </label>
              ))}
            </div>
          )}

          {type === 'input' && (
            <div className="mt-6">
              <input
                type="text"
                value={selectedValue}
                onChange={(e) => onChange(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                placeholder={t('progressiveForm.yourAnswer')}
              />
            </div>
          )}

          {type === 'select' && (
            <div className="mt-6">
              <select
                value={selectedValue}
                onChange={(e) => onChange(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">{t('progressiveForm.pleaseSelect')}</option>
                {answers.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value}
                  </option>
                ))}
              </select>
            </div>
          )}

          {type === 'checkbox' && (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {answers.map((option) => {
                const isChecked =
                  Array.isArray(selectedValue) &&
                  selectedValue.includes(option.value);

                const handleCheckboxChange = () => {
                  let newValue: string[] = Array.isArray(selectedValue)
                    ? [...selectedValue]
                    : [];
                  if (isChecked) {
                    newValue = newValue.filter((v) => v !== option.value);
                  } else {
                    newValue.push(option.value);
                  }
                  onChange(newValue);
                };

                return (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      value={option.value}
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                      className="form-checkbox text-blue-600"
                    />
                    {option.value}
                  </label>
                );
              })}
            </div>
          )}

          {/* Error message */}
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
      </div>

      {/* Buttons Section */}
      <div className="flex flex-col gap-4 border-t border-t-neutral-100 px-8 py-6 lg:flex-row lg:justify-end">
        {step > 1 && (
          <Button
            variant="secondary"
            sizes="small"
            name={t('progressiveForm.back')}
            leftIcon={{ name: 'chevron-left', size: '18px' }}
            onClick={onBack}
            className="w-full justify-center rounded px-4 py-2 text-sm transition-all duration-300 ease-in-out lg:w-auto"
          >
            {t('progressiveForm.back')}
          </Button>
        )}
        <Button
          variant="primary"
          sizes="small"
          name={t('progressiveForm.next')}
          rightIcon={{ name: 'arrow-right', size: '18px' }}
          onClick={handleNextClick}
          disabled={!isValid}
          className="w-full justify-center rounded px-4 py-2 text-sm text-white transition-all duration-300 ease-in-out disabled:cursor-not-allowed disabled:opacity-50 lg:w-auto"
        >
          {t('progressiveForm.next')}
        </Button>
      </div>
    </div>
  );
};

export default QuestionStep;
